import React, { startTransition } from 'react';
import type { PersonCommunicationData } from '@gov-nx/core/hooks';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { FormCheckboxProps, FormInputProps } from '@gov-nx/ui/types';
import {
	Button,
	FormCheckbox,
	FormInput,
	FormWidthElementLayout,
	Icon,
} from '@gov-nx/ui/web';
import { CommunicationDataFieldLayout } from './CommunicationDataFieldLayout';

interface UseCommunicationDataEmailProps {
	fields: {
		_useEmail: FormCheckboxProps;
		email: FormInputProps;
	};
	wcagLabels: {
		edit: string;
		cancel: string;
	};
	personCommunicationData: PersonCommunicationData;
	onReset: () => void;
	email?: string;
	isRequired?: boolean;
}

export const UseCommunicationDataEmail = ({
	personCommunicationData,
	email,
	wcagLabels,
	fields,
	onReset,
	isRequired,
}: UseCommunicationDataEmailProps) => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const isEditingEmail = personCommunicationData.control.isEditingEmail.value;
	const checkboxContent = (
		<>
			<FormCheckbox {...fields._useEmail} />
			{isRequired && <div className="gov-color--text-status-error">*</div>}
		</>
	);

	const editEmailButton = (
		<Button
			onClick={personCommunicationData.control.isEditingEmail.setTrue}
			isDisabled={fields.email.field.isDisabled}
			aria-label={wcagLabels.edit}
			color="primary"
			size="s"
			type="base"
			iconEnd={
				<Icon
					name="pencil-square"
					type="templates"
				/>
			}
		/>
	);

	return (
		<CommunicationDataFieldLayout
			isEditing={isEditingEmail}
			checkboxContent={checkboxContent}
			editButton={editEmailButton}
			valueContent={
				<strong className="break-all">
					{email ? email : <i>{t('empty')}</i>}
				</strong>
			}
			editContent={
				<div className="ml-8 flex flex-row gap-2 [ md:ml-10 md:w-1/2 ]">
					<FormWidthElementLayout type="l">
						<FormInput
							{...fields.email}
							label={{ className: 'hidden!' }}
						/>
					</FormWidthElementLayout>

					<Button
						color="primary"
						type="base"
						size="m"
						iconEnd={
							<Icon
								name="x-lg"
								type="components"
							/>
						}
						onClick={() => {
							personCommunicationData.control.isEditingEmail.setFalse();
							startTransition(() => {
								onReset();
							});
						}}
						isDisabled={fields.email.field.isDisabled}
						aria-label={wcagLabels.cancel}
					/>
				</div>
			}
		/>
	);
};
